import { useState, useRef, useEffect } from "react";
import jsQR from "jsqr";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
import api from "../utils/axios";
import toast from "react-hot-toast";
import { FaUpload, FaIdCard, FaAddressCard, FaTimes } from "react-icons/fa";

// Make sure you copied pdf.worker.min.mjs → public/pdf.worker.min.mjs
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const BASE_URL = "https://influencer-scheme.onrender.com";

const SHEET_KYC_URL =
  "https://script.google.com/macros/s/AKfycbxaCYZ_n4xBjbf1jFGbNLrQhkmzyaCacHAbKdW9fLa_paVGMXAUKsh55hBPtfbrfATXsw/exec";

const toIframeSrc = (url) => {
  if (!url) return "";

  const u = String(url).trim();

  // Google Drive view -> preview (iframe-friendly)
  // https://drive.google.com/file/d/<id>/view?usp=...
  const m = u.match(/drive\.google\.com\/file\/d\/([^/]+)\//i);
  if (m?.[1]) return `https://drive.google.com/file/d/${m[1]}/preview`;

  // Google Drive open?id=<id>
  const idMatch = u.match(/drive\.google\.com\/open\?id=([^&]+)/i);
  if (idMatch?.[1])
    return `https://drive.google.com/file/d/${idMatch[1]}/preview`;

  // For direct pdf/image links keep as-is
  return u;
};

export default function AddUser({ editUser = null, onClose, onSaved }) {
  const isEditMode = !!editUser;

  const [formData, setFormData] = useState({
    name: editUser?.name || "",
    mobile: editUser?.mobile || "",
    district: editUser?.district || "",
    type: editUser?.type || "",
    area: editUser?.area || "",
    address: editUser?.address || "",
    // profilePic: null,
    // documents: { pan: null, aadhar: null },
  });
  const [remoteKyc, setRemoteKyc] = useState({
    profilePicUrl: "",
    panUrl: "",
    aadharUrl: "",
  });
  const [preview, setPreview] = useState(
    editUser?.profilePic
      ? `${BASE_URL}${editUser.profilePic}`
      : "https://via.placeholder.com/150",
  );

  const [status, setStatus] = useState({
    pan: editUser?.documents?.pan ? "Uploaded" : "",
    aadhar: editUser?.documents?.aadhar ? "Uploaded" : "",
  });

  const [loading, setLoading] = useState(false);

  // Scroll to bottom button logic
  const bodyRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  useEffect(() => {
    if (editUser) {
      setFormData({
        name: editUser.name || "",
        mobile: editUser.mobile || "",
        district: editUser.district || "",
        type: editUser.type || "",
        area: editUser.area || "",
        address: editUser.address || "",
        // profilePic: null,
        // documents: { pan: null, aadhar: null },
      });
      setPreview(
        editUser.profilePic
          ? `${BASE_URL}${editUser.profilePic}`
          : "https://via.placeholder.com/150",
      );
      setStatus({
        pan: editUser.documents?.pan ? "Uploaded" : "",
        aadhar: editUser.documents?.aadhar ? "Uploaded" : "",
      });
    }
  }, [editUser]);

  useEffect(() => {
    const fetchKycFromSheet = async () => {
      const mobile = String(editUser?.mobile || "").trim();
      if (!mobile) return;

      try {
        const res = await fetch(SHEET_KYC_URL);
        if (!res.ok) throw new Error("Sheet fetch failed: " + res.status);

        const rows = await res.json();
        const arr = Array.isArray(rows) ? rows : [];

        // ✅ match by mobile (adjust key names if your sheet uses different headers)
        const match = arr.find((r) => {
          const m = String(
            r?.mobile ||
              r?.MOBILE ||
              r?.["MOBILE"] ||
              r?.["Mobile"] ||
              r?.["MOBILE NO"] ||
              "",
          )
            .replace(/\D/g, "")
            .slice(-10);

          return m === mobile;
        });

        if (!match) {
          setRemoteKyc({ profilePicUrl: "", panUrl: "", aadharUrl: "" });
          return;
        }

        const profilePicUrl =
          match?.profilePic ||
          match?.PROFILE_PIC ||
          match?.["PROFILE PIC"] ||
          match?.["PROFILEPIC"] ||
          "";

        const panUrl =
          match?.pan ||
          match?.PAN ||
          match?.["PAN DOC"] ||
          match?.["PAN_URL"] ||
          "";

        const aadharUrl =
          match?.aadhar ||
          match?.AADHAR ||
          match?.["AADHAR DOC"] ||
          match?.["AADHAR_URL"] ||
          "";

        const next = {
          profilePicUrl: String(profilePicUrl || "").trim(),
          panUrl: String(panUrl || "").trim(),
          aadharUrl: String(aadharUrl || "").trim(),
        };

        setRemoteKyc(next);

        // ✅ refill preview (iframe src)
        if (next.profilePicUrl) {
          setPreview(toIframeSrc(next.profilePicUrl));
        }

        // ✅ show uploaded labels
        setStatus((prev) => ({
          ...prev,
          pan: next.panUrl ? "Uploaded (from sheet)" : prev.pan,
          aadhar: next.aadharUrl ? "Uploaded (from sheet)" : prev.aadhar,
        }));
      } catch (e) {
        console.error("KYC sheet fetch error:", e);
      }
    };

    if (editUser?.mobile) fetchKycFromSheet();
  }, [editUser?.mobile]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleProfileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPG/JPEG or PNG allowed for profile photo!");
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({ ...prev, profilePic: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG or PDF allowed!");
      e.target.value = "";
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB)!");
      e.target.value = "";
      return;
    }

    setLoading(true);

    let isValid = false;
    let messages = [];
    let ocrText = "";
    let qrData = "";

    try {
      if (file.type !== "application/pdf") {
        // Image
        const img = await loadImageFromFile(file);
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // QR
        const qrResult = jsQR(
          imageData.data,
          imageData.width,
          imageData.height,
        );
        if (qrResult?.data) {
          qrData = qrResult.data.toUpperCase();
          messages.push("QR detected");
        }

        // OCR
        const {
          data: { text },
        } = await Tesseract.recognize(file, "eng+hin", {
          logger: (m) => console.log(m),
          tessedit_char_whitelist:
            "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz /:.,()-" +
            "आअइईउऊऋएऐओऔकखगघङचछजझञटठडढणतथदधनपफबभमयरलवशषसहक्षत्रज्ञ०१२३४५६७८९",
        });
        ocrText = text.toUpperCase().replace(/\s+/g, " ").trim();
      } else {
        // PDF - first page only
        messages.push("Processing PDF (page 1)...");
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        if (pdf.numPages < 1) throw new Error("Empty PDF");

        const page = await pdf.getPage(1);
        const scale = 2.5;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
        }).promise;

        const imageData = canvas
          .getContext("2d")
          .getImageData(0, 0, canvas.width, canvas.height);
        const qrResult = jsQR(imageData.data, canvas.width, canvas.height);
        if (qrResult?.data) {
          qrData = qrResult.data.toUpperCase();
          messages.push("QR detected on PDF");
        }

        const {
          data: { text },
        } = await Tesseract.recognize(
          canvas.toDataURL("image/png"),
          "eng+hin",
          {
            logger: (m) => console.log(m),
            tessedit_char_whitelist:
              "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz /:.,()-" +
              "आअइईउऊऋएऐओऔकखगघङचछजझञटठडढणतथदधनपफबभमयरलवशषसहक्षत्रज्ञ०१२३४५६७८९",
          },
        );
        ocrText = text.toUpperCase().replace(/\s+/g, " ").trim();
      }

      // Validation
      if (type === "pan") {
        const hasPan =
          ocrText.match(/[A-Z]{5}\d{4}[A-Z]/) ||
          qrData.match(/[A-Z]{5}\d{4}[A-Z]/);
        const hasKeyword =
          ocrText.includes("INCOME TAX") ||
          ocrText.includes("आयकर") ||
          ocrText.includes("PERMANENT ACCOUNT") ||
          ocrText.includes("स्थायी लेखा");

        if (hasPan || hasKeyword) {
          isValid = true;
          if (hasPan) messages.push("PAN pattern ✓");
          if (hasKeyword) messages.push("PAN keyword ✓");
        }
      } else if (type === "aadhar") {
        const hasAadhaarNum = ocrText.match(/\b\d{4}\s*\d{4}\s*\d{4}\b/);
        const hasKeyword =
          ocrText.includes("आधार") ||
          ocrText.includes("AADHAAR") ||
          ocrText.includes("UNIQUE IDENTIFICATION") ||
          ocrText.includes("GOVERNMENT OF INDIA") ||
          ocrText.includes("भारत सरकार");

        if (hasAadhaarNum || hasKeyword) {
          isValid = true;
          if (hasAadhaarNum) messages.push("Aadhaar number ✓");
          if (hasKeyword) messages.push("UIDAI keyword ✓");
        }
      }

      if (isValid) {
        setFormData((prev) => ({
          ...prev,
          documents: { ...prev.documents, [type]: file },
        }));
        setStatus((prev) => ({ ...prev, [type]: `${file.name} (validated)` }));
        toast.success(`${type.toUpperCase()} looks valid!`);
      } else {
        toast.error(
          `Invalid ${type.toUpperCase()} document.\n${messages.join("\n")}`,
        );
        e.target.value = "";
      }
    } catch (err) {
      console.error("Document validation failed:", err);
      toast.error("Error processing document. Try clearer image/PDF.");
      e.target.value = "";
    } finally {
      setLoading(false);
    }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    const dealer = JSON.parse(localStorage.getItem("dealer") || "{}");
    if (!dealer?.mobile) {
      toast.error("Session expired. Please login again.", { id: "save" });
      return;
    }
    if (!formData.name.trim()) return toast.error("Name is required!");
    if (formData.mobile.length !== 10)
      return toast.error("10-digit mobile required!");
    if (!formData.district.trim()) return toast.error("District required!");
    if (!formData.type.trim()) return toast.error("Type required!");
    if (!formData.area.trim()) return toast.error("Area required!");

    setLoading(true);
    toast.loading(isEditMode ? "Updating..." : "Adding...", { id: "save" });

    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("mobile", formData.mobile);
      payload.append("district", formData.district);
      payload.append("type", formData.type);
      payload.append("area", formData.area);
      payload.append("address", formData.address || "");
      payload.append("dealer", dealer.name || "");
      payload.append("dealergst", dealer.gst || "");

      const headers = { "x-auth-phone": dealer.mobile };

      // Attach files so the backend uploads them to Google Drive
      if (formData.profilePic) {
        payload.append(
          "profilePic",
          formData.profilePic,
          `profile_${Date.now()}_${formData.profilePic.name}`,
        );
      }
      if (formData.documents.pan) {
        payload.append(
          "pan",
          formData.documents.pan,
          `pan_${Date.now()}_${formData.documents.pan.name}`,
        );
      }
      if (formData.documents.aadhar) {
        payload.append(
          "aadhar",
          formData.documents.aadhar,
          `aadhar_${Date.now()}_${formData.documents.aadhar.name}`,
        );
      }

      if (isEditMode) {
        await api.put(`/users/${editUser._id}`, payload, { headers });
      } else {
        await api.post("/users/add", payload, { headers });
      }

      toast.success(
        isEditMode ? "Customer updated!" : "Customer added successfully!",
        { id: "save" },
      );

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.message ||
        (isEditMode ? "Update failed" : "Add failed");
      toast.error(msg, { id: "save" });
    } finally {
      setLoading(false);
    }
  };

  const districts = [
    "AHMEDABAD",
    "AMRELI",
    "ANAND",
    "ARAVALLI",
    "BANASKANTHA",
    "BHARUCH",
    "BHAVNAGAR",
    "BOTAD",
    "CHHOTA UDAIPUR",
    "DAHOD",
    "DANG",
    "DEVBHOOMI DWARKA",
    "GANDHINAGAR",
    "GIR SOMNATH",
    "JAMNAGAR",
    "JUNAGADH",
    "KHEDA",
    "KUTCH",
    "MAHISAGAR",
    "MEHSANA",
    "MORBI",
    "NARMADA",
    "NAVSARI",
    "PANCHMAHAL",
    "PATAN",
    "PORBANDAR",
    "RAJKOT",
    "SABARKANTHA",
    "SURAT",
    "SURENDRANAGAR",
    "TAPI",
    "VADODARA",
    "VALSAD",
  ];

  const DOC_UI = {
    pan: {
      label: "PAN",
      Icon: FaIdCard,
      wrap: "bg-blue-100 hover:bg-blue-200",
      icon: "text-blue-700",
    },
    aadhar: {
      label: "AADHAR",
      Icon: FaAddressCard,
      wrap: "bg-green-100 hover:bg-green-200",
      icon: "text-green-700",
    },
  };

  const updateScrollBtn = () => {
    const el = bodyRef.current;
    if (!el) return;
    setShowScrollBtn(
      el.scrollHeight > el.clientHeight + 10 &&
        el.scrollTop + el.clientHeight < el.scrollHeight - 10,
    );
  };

  useEffect(() => {
    updateScrollBtn();
    window.addEventListener("resize", updateScrollBtn);
    return () => window.removeEventListener("resize", updateScrollBtn);
  }, []);

  function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image failed to load"));
      img.src = URL.createObjectURL(file);
    });
  }

  return (
    <div className="w-full">
      <form
        onSubmit={saveUser}
        className="bg-white w-full max-w-full mx-auto flex flex-col border rounded-xl sm:rounded-2xl overflow-hidden"
        style={{ maxHeight: "calc(100vh - 140px)" }}
      >
        <div
          ref={bodyRef}
          onScroll={updateScrollBtn}
          className="flex-1 overflow-y-auto p-4 sm:p-8 relative"
        >
          {showScrollBtn && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 bg-yellow-600 hover:bg-yellow-700 text-white p-3 rounded-full shadow-lg z-10"
              title="Close"
            >
              <FaTimes />
            </button>
          )}

          {/* Profile + Documents */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
            <label className="cursor-pointer flex-shrink-0">
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleProfileUpload}
                className="hidden"
              />
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-yellow-400 shadow group">
                {preview ? (
                  <iframe
                    src={toIframeSrc(preview)}
                    title="Profile Preview"
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allow="autoplay"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-500">
                    No preview
                  </div>
                )}

                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition">
                  <FaUpload className="text-4xl mb-2" />
                  <span className="font-bold">
                    {isEditMode ? "Change" : "Add"} Photo
                  </span>
                </div>
              </div>
            </label>

            <div className="flex flex-col gap-3 mt-4 sm:mt-0 sm:ml-auto">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">
                  Documents (KYC compulsory)
                </span>
              </div>

              <div className="flex gap-6 justify-center sm:justify-end">
                {["pan", "aadhar"].map((key) => {
                  const { label, Icon, wrap, icon } = DOC_UI[key];

                  const iframeUrl =
                    key === "pan"
                      ? toIframeSrc(remoteKyc.panUrl)
                      : toIframeSrc(remoteKyc.aadharUrl);

                  return (
                    <div key={key} className="flex flex-col items-center">
                      {/* Upload Button */}
                      <label className="cursor-pointer flex flex-col items-center">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          onChange={(e) => handleFileUpload(e, key)}
                          className="hidden"
                          disabled={loading}
                        />

                        <div
                          className={`w-16 h-16 rounded-lg flex items-center justify-center ${wrap} transition ${
                            loading ? "opacity-50" : "hover:scale-105"
                          }`}
                        >
                          <Icon className={`text-3xl ${icon}`} />
                        </div>

                        <span className="mt-2 text-sm font-semibold">
                          {label}
                        </span>

                        {status[key] && (
                          <span className="mt-1 text-xs text-green-600 truncate max-w-[120px] text-center">
                            {status[key]}
                          </span>
                        )}
                      </label>

                      {/* ✅ IFRAME PREVIEW FROM JSON */}
                      {iframeUrl && (
                        <div className="mt-3 w-40 h-40 border rounded-lg overflow-hidden shadow-sm">
                          <iframe
                            src={iframeUrl}
                            title={`${label} preview`}
                            className="w-full h-full"
                            style={{ border: 0 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column */}
            <div className="space-y-5">
              <div>
                <label className="block font-semibold mb-1">Full Name *</label>
                <input
                  id="name"
                  value={formData.name}
                  placeholder="Enter Full Name"
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Type *</label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400"
                  required
                >
                  <option value="">Select Type</option>
                  <option>Kadiya</option>
                  <option>Mason</option>
                  <option>Contractor</option>
                  <option>Architect</option>
                  <option>Builder</option>
                  <option>Civil Engineer / Consultant</option>
                  <option>Fabricator</option>
                  <option>End User (Individual House)</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">District *</label>
                <select
                  id="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400"
                  required
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0) + d.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-5">
              <div>
                <label className="block font-semibold mb-1">
                  Mobile (WhatsApp) *
                </label>
                <input
                  id="mobile"
                  value={formData.mobile}
                  placeholder="Enter Mobile Number"
                  onChange={(e) => {
                    // ✅ block edits completely in edit mode
                    if (isEditMode) return;

                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData((p) => ({ ...p, mobile: val }));
                  }}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400 ${
                    isEditMode
                      ? "bg-gray-100 text-gray-600 cursor-not-allowed"
                      : ""
                  }`}
                  maxLength={10}
                  required
                  disabled={isEditMode} // ✅ main lock
                  readOnly={isEditMode} // ✅ extra safety (some browsers)
                  inputMode="numeric"
                  pattern="\d{10}"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">
                  Area / Locality *
                </label>
                <input
                  id="area"
                  placeholder="Enter Area/Locality"
                  value={formData.area}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Full Address</label>
                <textarea
                  id="address"
                  value={formData.address}
                  placeholder="Enter Address"
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-8 bg-gray-50 border-t flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border rounded-xl font-semibold hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold rounded-xl shadow hover:from-yellow-600 hover:to-yellow-700 disabled:opacity-60"
          >
            {loading
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "UPDATE CUSTOMER"
                : "SAVE CUSTOMER"}
          </button>
        </div>
      </form>
    </div>
  );
}
